import {DataStatus} from 'platform/components';
import {Box, Show, useElementSize} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCheckoutByIdQuery, useGetCheckoutLinkForServiceOrderQuery} from '@dms/api/sales';
import {useGetServiceOrderContractQuery, useGetServiceOrderInsuranceQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {BoschFleetIntegrationCard} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CustomerContractSection} from './CustomerContractSection';
import {DiscountSection} from './DiscountSection';
import {VehicleClassSection} from './VehicleClassSection';

// Tab width in px. Below it the first column gets too narrow for the customer contract card.
const SIDE_BY_SIDE_MIN_WIDTH = 1100;

interface DiscountsProps extends RequiredTestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  isReadOnly?: boolean;
  canEditDiscount: boolean;
  canEditCustomerContract: boolean;
  canEditVehicleClass: boolean;
  canEditCustomSmallUnitMaterial: boolean;
  orderType: string | undefined | null;
}

export function Discounts(props: DiscountsProps) {
  const [containerRef, containerWidth] = useElementSize<HTMLDivElement>();

  const {
    data: serviceOrderInsurance,
    isFetching: isServiceOrderInsuranceFetching,
    isError: isServiceOrderInsuranceError,
  } = useGetServiceOrderInsuranceQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    },
    {skip: props.orderType !== 'INS'}
  );
  const {
    data: serviceOrderContract,
    isFetching: isServiceOrderContractFetching,
    isError: isServiceOrderContractError,
  } = useGetServiceOrderContractQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    },
    {skip: props.orderType !== 'CON'}
  );
  const {data: checkout} = useGetCheckoutLinkForServiceOrderQuery(
    {serviceOrderId: props.serviceOrderId},
    {refetchOnMountOrArgChange: true}
  );
  const {data: checkoutData} = useGetCheckoutByIdQuery(
    {checkoutId: checkout?.checkoutId ?? ''},
    {skip: !checkout?.checkoutId}
  );

  const isAssignCustomerContractDisabled = match(props.orderType)
    .with('CON', () => isNilOrEmpty(serviceOrderContract?.provider))
    .with('INS', () => isNilOrEmpty(serviceOrderInsurance?.insuranceCompany))
    .otherwise(() => false);

  const isLoading = isServiceOrderInsuranceFetching || isServiceOrderContractFetching;
  const isError = isServiceOrderInsuranceError || isServiceOrderContractError;

  const isSideBySide = isNotNil(containerWidth) && containerWidth >= SIDE_BY_SIDE_MIN_WIDTH;

  return (
    <Box ref={containerRef}>
      <DataStatus isLoading={isLoading} isError={isError}>
        <DiscountSection
          serviceCaseId={props.serviceCaseId}
          serviceOrderId={props.serviceOrderId}
          isReadOnly={props.isReadOnly}
          canEditDiscount={props.canEditDiscount}
          canEditCustomSmallUnitMaterial={props.canEditCustomSmallUnitMaterial}
          isSideBySide={isSideBySide}
          context={
            <>
              <CustomerContractSection
                orderId={props.serviceOrderId}
                serviceCaseId={props.serviceCaseId}
                isReadOnly={props.isReadOnly}
                isAssignDisabled={isAssignCustomerContractDisabled}
                canEditCustomerContract={props.canEditCustomerContract}
                summaryColumns={isSideBySide ? 2 : 4}
                data-testid={suffixTestId('customerContract', props)}
              />
              <VehicleClassSection
                serviceCaseId={props.serviceCaseId}
                serviceOrderId={props.serviceOrderId}
                isReadOnly={props.isReadOnly}
                canEditVehicleClass={props.canEditVehicleClass}
                data-testid={suffixTestId('vehicleClass', props)}
              />
              <Show
                when={
                  checkoutData?.billingInformation?.customerInformation?.id
                    ? checkoutData.billingInformation.customerInformation.id
                    : null
                }
              >
                {(customerId) => (
                  <BoschFleetIntegrationCard
                    cardVariant="inlineGrey"
                    subTitle={i18n.t('general.labels.additionalInformation')}
                    customerId={customerId}
                  />
                )}
              </Show>
            </>
          }
          data-testid={suffixTestId('discounts', props)}
        />
      </DataStatus>
    </Box>
  );
}
